import {
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TablePagination,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded"
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded"
import WarehouseRoundedIcon from "@mui/icons-material/WarehouseRounded"
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded"

import MainLayout from "../../layouts/MainLayout"

import {
  createStockBalance,
  deleteStockBalance,
  getStockBalanceById,
  getStockBalanceFormData,
  getStockBalances,
  updateStockBalance,
} from "../../services/stockBalanceService"


// =========================================================
// CONSTANTS
// =========================================================

const ROWS_PER_PAGE = 10


const defaultFormData = {
  company_id: 1,
  product_id: "",
  location_type: "store",
  store_id: "",
  warehouse_id: "",
  quantity: "",
  reserved_quantity: 0,
}


const STOCK_WRITE_ROLES = [
  "platform_admin",
  "super_admin",
  "dg",
  "gestionnaire_operations",
  "gestionnaire_stock",
  "stock_maison_djo",
  "stock_depot_relais",
  "admin_maison_djo",
  "admin_depot_relais",
]


const READ_ONLY_STOCK_ROLES = [
  "gerant_magasin",
  "gerant_depot_relais",
  "agent_commercial",
  "caissier_central",
]


// =========================================================
// HELPERS
// =========================================================

function getLocalValue(
  key,
  fallback = ""
) {
  const value =
    localStorage.getItem(key)

  return value &&
    value !== "undefined" &&
    value !== "null"
    ? value
    : fallback
}


function canWriteStock(role) {
  if (
    READ_ONLY_STOCK_ROLES.includes(
      role
    )
  ) {
    return false
  }

  return STOCK_WRITE_ROLES.includes(
    role
  )
}


function toNumber(
  value,
  fallback = 0
) {
  const parsed =
    Number(value)

  return Number.isFinite(parsed)
    ? parsed
    : fallback
}


function formatQuantity(value) {
  return toNumber(value).toLocaleString(
    "fr-FR",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )
}


function getLocationType(row) {
  return String(
    row?.location_type ||
      (
        row?.store_id
          ? "store"
          : row?.warehouse_id
          ? "warehouse"
          : ""
      )
  ).toLowerCase()
}


function getLocationName(row) {
  return (
    row?.store_name ||
    row?.warehouse_name ||
    "-"
  )
}


function getLocationLabel(row) {
  const value =
    getLocationType(row)

  if (
    value === "warehouse"
  ) {
    return "Entrepôt"
  }

  if (
    value === "store"
  ) {
    return "Shop"
  }

  return "-"
}


function getLocationChip(row) {
  const value =
    getLocationType(row)

  if (
    value === "warehouse"
  ) {
    return (
      <Chip
        label="Entrepôt"
        color="info"
        size="small"
        variant="outlined"
        sx={{
          height: 24,
          fontWeight: 800,
        }}
      />
    )
  }

  if (
    value === "store"
  ) {
    return (
      <Chip
        label="Shop"
        color="primary"
        size="small"
        variant="outlined"
        sx={{
          height: 24,
          fontWeight: 800,
        }}
      />
    )
  }

  return (
    <Chip
      label="-"
      size="small"
      variant="outlined"
      sx={{
        height: 24,
      }}
    />
  )
}


function getErrorMessage(
  error,
  fallback
) {
  const detail =
    error?.response?.data?.detail

  if (
    typeof detail === "string"
  ) {
    return detail
  }

  if (
    Array.isArray(detail)
  ) {
    return detail
      .map(
        (item) =>
          item?.msg ||
          JSON.stringify(item)
      )
      .join(" | ")
  }

  if (
    detail &&
    typeof detail === "object"
  ) {
    return (
      detail.message ||
      JSON.stringify(detail)
    )
  }

  return (
    error?.message ||
    fallback
  )
}


// =========================================================
// KPI CARD
// =========================================================

function MetricCard({
  label,
  value,
  caption,
  icon,
  accent = "#2563eb",
  background = "rgba(37, 99, 235, 0.09)",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: {
          xs: 1.35,
          sm: 1.5,
        },

        py: 1.15,

        minWidth: 0,

        borderRadius: 3,

        borderColor:
          "rgba(148, 163, 184, 0.22)",

        bgcolor: "#ffffff",

        boxShadow:
          "0 6px 18px rgba(15, 23, 42, 0.035)",
      }}
    >
      <Box
        sx={{
          display: "flex",

          alignItems: "center",

          gap: 1.15,

          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,

            flexShrink: 0,

            display: "grid",
            placeItems: "center",

            borderRadius: 2.5,

            color: accent,

            bgcolor: background,
          }}
        >
          {icon}
        </Box>


        <Box
          sx={{
            minWidth: 0,
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",

              alignItems: "baseline",

              gap: 0.7,

              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: 19,
                  sm: 21,
                },

                lineHeight: 1,

                fontWeight: 900,

                color: "#0f172a",

                whiteSpace: "nowrap",
              }}
            >
              {value}
            </Typography>


            <Typography
              variant="body2"
              sx={{
                minWidth: 0,

                fontWeight: 800,

                color: "#334155",

                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
          </Box>


          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",

              mt: 0.35,

              lineHeight: 1.25,

              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {caption}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}


// =========================================================
// MOBILE CARD
// =========================================================

function StockMobileCard({
  row,
  canManage,
  onEdit,
  onDelete,
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,

        borderColor:
          "rgba(148, 163, 184, 0.22)",

        boxShadow:
          "0 5px 16px rgba(15, 23, 42, 0.025)",
      }}
    >
      <CardContent
        sx={{
          p: 1.5,

          "&:last-child": {
            pb: 1.5,
          },
        }}
      >
        <Stack spacing={1.35}>
          <Box
            sx={{
              display: "flex",

              justifyContent:
                "space-between",

              alignItems:
                "flex-start",

              gap: 1,
            }}
          >
            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  overflowWrap:
                    "anywhere",
                }}
              >
                {row.product_name ||
                  "-"}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                {row.product_code ||
                  "-"}
              </Typography>
            </Box>

            {getLocationChip(row)}
          </Box>


          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
            >
              Emplacement
            </Typography>

            <Typography
              variant="body2"
              fontWeight={800}
              sx={{
                overflowWrap:
                  "anywhere",
              }}
            >
              {getLocationName(row)}
            </Typography>
          </Box>


          <Box
            sx={{
              display: "grid",

              gridTemplateColumns:
                "repeat(3, minmax(0, 1fr))",

              gap: 0.8,
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: 0.9,

                borderRadius: 2.5,

                bgcolor: "#f8fafc",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Total
              </Typography>

              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                {formatQuantity(
                  row.quantity
                )}
              </Typography>
            </Paper>


            <Paper
              variant="outlined"
              sx={{
                p: 0.9,

                borderRadius: 2.5,

                bgcolor: "#f8fafc",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Réservée
              </Typography>

              <Typography
                fontWeight={900}
                color="warning.main"
              >
                {formatQuantity(
                  row.reserved_quantity
                )}
              </Typography>
            </Paper>


            <Paper
              variant="outlined"
              sx={{
                p: 0.9,

                borderRadius: 2.5,

                bgcolor: "#f8fafc",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Disponible
              </Typography>

              <Typography
                fontWeight={900}
                color={
                  toNumber(
                    row.available_quantity
                  ) > 0
                    ? "success.main"
                    : "error.main"
                }
              >
                {formatQuantity(
                  row.available_quantity
                )}
              </Typography>
            </Paper>
          </Box>


          {canManage ? (
            <Box
              sx={{
                display: "grid",

                gridTemplateColumns: {
                  xs: "1fr",

                  sm:
                    "repeat(2, minmax(0, 1fr))",
                },

                gap: 0.75,
              }}
            >
              <Button
                variant="contained"
                size="small"
                disableElevation
                onClick={() =>
                  onEdit(row.id)
                }
              >
                Modifier
              </Button>

              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() =>
                  onDelete(row.id)
                }
              >
                Supprimer
              </Button>
            </Box>
          ) : (
            <Chip
              label="Lecture seule"
              size="small"
              variant="outlined"
              sx={{
                alignSelf:
                  "flex-start",
              }}
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}


// =========================================================
// PAGE
// =========================================================

function StockBalancesPage() {
  const theme =
    useTheme()

  const isMdDown =
    useMediaQuery(
      theme.breakpoints.down("md")
    )

  const isSmDown =
    useMediaQuery(
      theme.breakpoints.down("sm")
    )


  // =======================================================
  // CURRENT CONTEXT
  // =======================================================

  const currentRole =
    getLocalValue(
      "kongosys_role",
      "viewer"
    )

  const currentStoreName =
    getLocalValue(
      "kongosys_store_name",
      ""
    )

  const currentOrganizationName =
    getLocalValue(
      "kongosys_organization_name",
      ""
    )

  const canManageStock =
    canWriteStock(
      currentRole
    )


  // =======================================================
  // DATA
  // =======================================================

  const [
    stockBalances,
    setStockBalances,
  ] = useState([])

  const [
    products,
    setProducts,
  ] = useState([])

  const [
    stores,
    setStores,
  ] = useState([])

  const [
    warehouses,
    setWarehouses,
  ] = useState([])


  // =======================================================
  // UI STATE
  // =======================================================

  const [
    loading,
    setLoading,
  ] = useState(false)

  const [
    saving,
    setSaving,
  ] = useState(false)

  const [
    open,
    setOpen,
  ] = useState(false)

  const [
    selectedStockBalance,
    setSelectedStockBalance,
  ] = useState(null)

  const [
    formData,
    setFormData,
  ] = useState({
    ...defaultFormData,
  })


  // =======================================================
  // FILTERS
  // =======================================================

  const [
    search,
    setSearch,
  ] = useState("")

  const [
    locationFilter,
    setLocationFilter,
  ] = useState("")


  // =======================================================
  // PAGINATION
  // =======================================================

  const [
    paginationModel,
    setPaginationModel,
  ] = useState({
    page: 0,
    pageSize:
      ROWS_PER_PAGE,
  })


  // =======================================================
  // SNACKBAR
  // =======================================================

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message:
        String(
          message || ""
        ),
    })
  }


  const handleCloseSnackbar =
    () => {
      setSnackbar(
        (previous) => ({
          ...previous,
          open: false,
        })
      )
    }


  // =======================================================
  // LOAD STOCK
  // =======================================================

  const loadStockBalances =
    async () => {
      try {
        setLoading(true)

        const data =
          await getStockBalances()

        setStockBalances(
          Array.isArray(data)
            ? data
            : []
        )
      } catch (error) {
        console.error(
          "Error loading stock balances:",
          error
        )

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de charger le stock actuel."
          ),
          "error"
        )
      } finally {
        setLoading(false)
      }
    }


  // =======================================================
  // LOAD FORM DATA
  // =======================================================

  const loadFormData =
    async () => {
      try {
        const data =
          await getStockBalanceFormData()

        setProducts(
          Array.isArray(
            data?.products
          )
            ? data.products
            : []
        )

        setStores(
          Array.isArray(
            data?.stores
          )
            ? data.stores
            : []
        )

        setWarehouses(
          Array.isArray(
            data?.warehouses
          )
            ? data.warehouses
            : []
        )
      } catch (error) {
        console.error(
          "Error loading form data:",
          error
        )

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de charger les données du formulaire stock."
          ),
          "error"
        )
      }
    }


  useEffect(() => {
    loadStockBalances()
    loadFormData()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  // =======================================================
  // FORM
  // =======================================================

  const resetForm = () => {
    setSelectedStockBalance(
      null
    )

    setFormData({
      ...defaultFormData,
    })
  }


  const openAddDialog = () => {
    if (
      !canManageStock
    ) {
      showSnackbar(
        "Lecture seule : votre rôle ne peut pas créer ou modifier le stock.",
        "warning"
      )

      return
    }

    resetForm()
    setOpen(true)
  }


  const openEditDialog =
    async (
      stockBalanceId
    ) => {
      if (
        !canManageStock
      ) {
        showSnackbar(
          "Lecture seule : votre rôle ne peut pas modifier le stock.",
          "warning"
        )

        return
      }

      try {
        const stockBalance =
          await getStockBalanceById(
            stockBalanceId
          )

        setSelectedStockBalance(
          stockBalance
        )

        setFormData({
          company_id:
            stockBalance.company_id ??
            1,

          product_id:
            stockBalance.product_id ??
            "",

          location_type:
            stockBalance.store_id
              ? "store"
              : "warehouse",

          store_id:
            stockBalance.store_id ??
            "",

          warehouse_id:
            stockBalance.warehouse_id ??
            "",

          quantity:
            stockBalance.quantity !==
              null &&
            stockBalance.quantity !==
              undefined
              ? Number(
                  stockBalance.quantity
                )
              : "",

          reserved_quantity:
            stockBalance.reserved_quantity !==
              null &&
            stockBalance.reserved_quantity !==
              undefined
              ? Number(
                  stockBalance.reserved_quantity
                )
              : 0,
        })

        setOpen(true)
      } catch (error) {
        console.error(error)

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de charger le détail du stock."
          ),
          "error"
        )
      }
    }


  const handleClose = () => {
    if (saving) {
      return
    }

    setOpen(false)
    resetForm()
  }


  const handleChange =
    (event) => {
      const {
        name,
        value,
      } =
        event.target

      setFormData(
        (previous) => {
          const updated = {
            ...previous,

            [name]:
              [
                "company_id",
                "product_id",
                "store_id",
                "warehouse_id",
              ].includes(name)
                ? value === ""
                  ? ""
                  : Number(value)
                : [
                    "quantity",
                    "reserved_quantity",
                  ].includes(name)
                ? value === ""
                  ? ""
                  : Number(value)
                : value,
          }

          if (
            name ===
            "location_type"
          ) {
            if (
              value === "store"
            ) {
              updated.warehouse_id =
                ""
            } else if (
              value ===
              "warehouse"
            ) {
              updated.store_id =
                ""
            }
          }

          return updated
        }
      )
    }


  // =======================================================
  // VALIDATION
  // =======================================================

  const validateForm = () => {
    if (
      !canManageStock
    ) {
      showSnackbar(
        "Action non autorisée pour ce rôle.",
        "warning"
      )

      return false
    }


    if (
      !formData.product_id
    ) {
      showSnackbar(
        "Le produit est obligatoire.",
        "warning"
      )

      return false
    }


    if (
      formData.location_type ===
        "store" &&
      !formData.store_id
    ) {
      showSnackbar(
        "Le shop est obligatoire.",
        "warning"
      )

      return false
    }


    if (
      formData.location_type ===
        "warehouse" &&
      !formData.warehouse_id
    ) {
      showSnackbar(
        "L'entrepôt est obligatoire.",
        "warning"
      )

      return false
    }


    if (
      formData.quantity ===
        "" ||
      Number(
        formData.quantity
      ) < 0
    ) {
      showSnackbar(
        "La quantité doit être un nombre valide.",
        "warning"
      )

      return false
    }


    if (
      formData.reserved_quantity ===
        "" ||
      Number(
        formData.reserved_quantity
      ) < 0
    ) {
      showSnackbar(
        "La quantité réservée doit être un nombre valide.",
        "warning"
      )

      return false
    }


    if (
      Number(
        formData.reserved_quantity
      ) >
      Number(
        formData.quantity
      )
    ) {
      showSnackbar(
        "La quantité réservée ne peut pas dépasser la quantité totale.",
        "warning"
      )

      return false
    }

    return true
  }


  // =======================================================
  // SAVE
  // =======================================================

  const handleSave =
    async () => {
      if (
        !validateForm()
      ) {
        return
      }

      const payload = {
        company_id:
          Number(
            formData.company_id ||
              1
          ),

        product_id:
          Number(
            formData.product_id
          ),

        store_id:
          formData.location_type ===
            "store" &&
          formData.store_id
            ? Number(
                formData.store_id
              )
            : null,

        warehouse_id:
          formData.location_type ===
            "warehouse" &&
          formData.warehouse_id
            ? Number(
                formData.warehouse_id
              )
            : null,

        quantity:
          Number(
            formData.quantity ||
              0
          ),

        reserved_quantity:
          Number(
            formData.reserved_quantity ||
              0
          ),
      }

      try {
        setSaving(true)

        if (
          selectedStockBalance?.id
        ) {
          await updateStockBalance(
            selectedStockBalance.id,
            payload
          )

          showSnackbar(
            "Stock modifié avec succès."
          )
        } else {
          await createStockBalance(
            payload
          )

          showSnackbar(
            "Stock créé avec succès."
          )
        }

        /*
         * Fermeture directe :
         * handleClose() bloque pendant saving === true.
         */
        setOpen(false)
        resetForm()

        await loadStockBalances()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible d'enregistrer le stock."
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // DELETE
  // =======================================================

  const handleDelete =
    async (
      stockBalanceId
    ) => {
      if (
        !canManageStock
      ) {
        showSnackbar(
          "Lecture seule : votre rôle ne peut pas supprimer le stock.",
          "warning"
        )

        return
      }

      const confirmed =
        window.confirm(
          "Voulez-vous vraiment supprimer cette ligne de stock ?"
        )

      if (!confirmed) {
        return
      }

      try {
        await deleteStockBalance(
          stockBalanceId
        )

        showSnackbar(
          "Ligne de stock supprimée avec succès."
        )

        await loadStockBalances()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de supprimer le stock."
          ),
          "error"
        )
      }
    }


  // =======================================================
  // FILTER
  // =======================================================

  const filteredStockBalances =
    useMemo(() => {
      const keyword =
        search
          .trim()
          .toLowerCase()

      return stockBalances.filter(
        (row) => {
          const locationType =
            getLocationType(row)

          const matchesSearch =
            keyword === "" ||
            [
              row.product_code,
              row.product_name,
              row.store_name,
              row.warehouse_name,
              getLocationLabel(row),
            ].some(
              (value) =>
                String(
                  value || ""
                )
                  .toLowerCase()
                  .includes(
                    keyword
                  )
            )

          const matchesLocation =
            locationFilter === "" ||
            locationType ===
              locationFilter

          return (
            matchesSearch &&
            matchesLocation
          )
        }
      )
    }, [
      stockBalances,
      search,
      locationFilter,
    ])


  // =======================================================
  // SUMMARY
  // =======================================================

  const summary =
    useMemo(() => {
      return filteredStockBalances.reduce(
        (
          accumulator,
          row
        ) => {
          accumulator.lines += 1

          accumulator.quantity +=
            toNumber(
              row.quantity
            )

          accumulator.reserved +=
            toNumber(
              row.reserved_quantity
            )

          accumulator.available +=
            toNumber(
              row.available_quantity
            )

          if (
            getLocationType(
              row
            ) === "warehouse"
          ) {
            accumulator.warehouses +=
              1
          }

          if (
            getLocationType(
              row
            ) === "store"
          ) {
            accumulator.stores +=
              1
          }

          return accumulator
        },
        {
          lines: 0,
          quantity: 0,
          reserved: 0,
          available: 0,
          stores: 0,
          warehouses: 0,
        }
      )
    }, [
      filteredStockBalances,
    ])


  // =======================================================
  // PAGINATION
  // =======================================================

  useEffect(() => {
    setPaginationModel(
      (previous) => ({
        ...previous,

        page: 0,
        pageSize:
          ROWS_PER_PAGE,
      })
    )
  }, [
    search,
    locationFilter,
  ])


  const paginatedMobileRows =
    useMemo(() => {
      const start =
        paginationModel.page *
        ROWS_PER_PAGE

      return filteredStockBalances.slice(
        start,
        start +
          ROWS_PER_PAGE
      )
    }, [
      filteredStockBalances,
      paginationModel.page,
    ])


  useEffect(() => {
    const totalPages =
      Math.max(
        1,
        Math.ceil(
          filteredStockBalances.length /
            ROWS_PER_PAGE
        )
      )

    if (
      paginationModel.page >=
      totalPages
    ) {
      setPaginationModel(
        (previous) => ({
          ...previous,

          page:
            totalPages - 1,

          pageSize:
            ROWS_PER_PAGE,
        })
      )
    }
  }, [
    filteredStockBalances.length,
    paginationModel.page,
  ])


  // =======================================================
  // RESET FILTERS
  // =======================================================

  const resetFilters = () => {
    setSearch("")
    setLocationFilter("")

    setPaginationModel({
      page: 0,
      pageSize:
        ROWS_PER_PAGE,
    })
  }


  // =======================================================
  // DESKTOP COLUMNS
  // =======================================================

  const columns =
    useMemo(
      () => [
        {
          field: "product",

          headerName:
            "Produit",

          minWidth: 185,

          flex: 1.35,

          sortable: false,

          renderCell:
            (params) => (
              <Stack
                spacing={0.15}

                justifyContent="center"

                sx={{
                  width: "100%",
                  height: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={900}
                  color="#0f172a"
                  noWrap
                  title={
                    params.row.product_name ||
                    ""
                  }
                >
                  {params.row.product_name ||
                    "-"}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                >
                  {params.row.product_code ||
                    "-"}
                </Typography>
              </Stack>
            ),
        },


        {
          field:
            "location",

          headerName:
            "Emplacement",

          minWidth: 175,

          flex: 1.2,

          sortable: false,

          renderCell:
            (params) => (
              <Stack
                spacing={0.3}

                justifyContent="center"

                sx={{
                  width: "100%",
                  height: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={800}
                  noWrap
                  title={
                    getLocationName(
                      params.row
                    )
                  }
                >
                  {getLocationName(
                    params.row
                  )}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    minHeight: 24,
                  }}
                >
                  {getLocationChip(
                    params.row
                  )}
                </Box>
              </Stack>
            ),
        },


        {
          field:
            "quantity",

          headerName:
            "Quantité",

          width: 92,

          align: "right",

          headerAlign:
            "right",

          renderCell:
            (params) => (
              <Typography
                variant="body2"
                fontWeight={900}
                sx={{
                  width: "100%",
                  textAlign: "right",
                }}
              >
                {formatQuantity(
                  params.value
                )}
              </Typography>
            ),
        },


        {
          field:
            "reserved_quantity",

          headerName:
            "Réservée",

          width: 92,

          align: "right",

          headerAlign:
            "right",

          renderCell:
            (params) => (
              <Typography
                variant="body2"
                fontWeight={800}
                color="warning.main"
                sx={{
                  width: "100%",
                  textAlign: "right",
                }}
              >
                {formatQuantity(
                  params.value
                )}
              </Typography>
            ),
        },


        {
          field:
            "available_quantity",

          headerName:
            "Disponible",

          width: 104,

          align: "right",

          headerAlign:
            "right",

          renderCell:
            (params) => (
              <Typography
                variant="body2"
                fontWeight={900}
                color={
                  toNumber(
                    params.value
                  ) > 0
                    ? "success.main"
                    : "error.main"
                }
                sx={{
                  width: "100%",
                  textAlign: "right",
                }}
              >
                {formatQuantity(
                  params.value
                )}
              </Typography>
            ),
        },


        {
          field: "actions",

          headerName:
            "Actions",

          width:
            canManageStock
              ? 178
              : 128,

          sortable: false,

          filterable: false,

          disableColumnMenu:
            true,

          renderCell:
            (params) =>
              canManageStock ? (
                <Stack
                  direction="row"
                  spacing={0.55}

                  alignItems="center"

                  sx={{
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    disableElevation
                    onClick={() =>
                      openEditDialog(
                        params.row.id
                      )
                    }
                    sx={{
                      minWidth: 74,
                      px: 1,
                      whiteSpace:
                        "nowrap",
                    }}
                  >
                    Modifier
                  </Button>

                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() =>
                      handleDelete(
                        params.row.id
                      )
                    }
                    sx={{
                      minWidth: 82,
                      px: 0.9,
                      whiteSpace:
                        "nowrap",
                    }}
                  >
                    Supprimer
                  </Button>
                </Stack>
              ) : (
                <Chip
                  label="Lecture seule"
                  size="small"
                  variant="outlined"
                />
              ),
        },
      ],
      [
        canManageStock,
      ]
    )


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          width: "100%",

          minWidth: 0,

          pb: {
            xs: 2,
            md: 4,
          },

          "& .MuiButton-root":
            {
              minHeight: 40,

              borderRadius: 2.5,

              textTransform:
                "none",

              fontWeight: 800,

              boxShadow: "none",
            },

          "& .MuiOutlinedInput-root":
            {
              borderRadius: 2.5,

              bgcolor: "#ffffff",
            },

          "& .MuiChip-root":
            {
              fontWeight: 800,
            },

          "& .MuiPaper-root":
            {
              backgroundImage:
                "none",
            },
        }}
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",

              sm:
                "minmax(0, 1fr) auto",
            },

            gap: 1.5,

            alignItems: "start",

            mb: 2,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              variant="h4"

              sx={{
                fontWeight: 900,

                color: "#0f172a",

                lineHeight: 1.12,

                letterSpacing:
                  "-0.025em",

                fontSize: {
                  xs: "1.65rem",
                  sm: "1.95rem",
                  md: "2.1rem",
                },
              }}
            >
              Stock actuel
            </Typography>


            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.4,

                lineHeight: 1.5,

                maxWidth: 760,
              }}
            >
              Consultation des quantités disponibles,
              réservées et totales par shop et par
              entrepôt.
            </Typography>


            {(currentOrganizationName ||
              currentStoreName) && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  mt: 0.35,
                }}
              >
                {currentOrganizationName ||
                  "Organisation"}

                {currentStoreName
                  ? ` • ${currentStoreName}`
                  : ""}
              </Typography>
            )}
          </Box>


          {canManageStock && (
            <Button
              variant="contained"

              disableElevation

              onClick={
                openAddDialog
              }

              sx={{
                minWidth: {
                  xs: "100%",
                  sm: 170,
                },

                justifySelf: {
                  xs: "stretch",
                  sm: "end",
                },
              }}
            >
              + Ajouter stock
            </Button>
          )}
        </Box>


        {/* =================================================
            READ ONLY INFO
        ================================================= */}

        {!canManageStock && (
          <Alert
            severity="info"
            variant="outlined"
            sx={{
              mb: 1.6,

              py: 0.35,

              borderRadius: 3,
            }}
          >
            Votre compte est en lecture seule sur cette page.
            Les gérants peuvent consulter le stock de leur
            point de vente, mais ne peuvent pas modifier
            manuellement les quantités.
          </Alert>
        )}


        {/* =================================================
            KPI
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",

              sm:
                "repeat(2, minmax(0, 1fr))",

              md:
                "repeat(4, minmax(0, 1fr))",
            },

            gap: 1.1,

            mb: 1.6,
          }}
        >
          <MetricCard
            label="Lignes"

            value={
              summary.lines
            }

            caption={`${summary.stores} shop(s) • ${summary.warehouses} entrepôt(s)`}

            accent="#2563eb"

            background="rgba(37, 99, 235, 0.09)"

            icon={
              <Inventory2RoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Quantité totale"

            value={
              formatQuantity(
                summary.quantity
              )
            }

            caption="Stock enregistré"

            accent="#0284c7"

            background="rgba(2, 132, 199, 0.09)"

            icon={
              <WarehouseRoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Réservée"

            value={
              formatQuantity(
                summary.reserved
              )
            }

            caption="Stock déjà affecté"

            accent="#d97706"

            background="rgba(217, 119, 6, 0.09)"

            icon={
              <PointOfSaleRoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Disponible"

            value={
              formatQuantity(
                summary.available
              )
            }

            caption="Stock réellement utilisable"

            accent="#16a34a"

            background="rgba(22, 163, 74, 0.09)"

            icon={
              <CheckCircleRoundedIcon fontSize="small" />
            }
          />
        </Box>


        {/* =================================================
            FILTERS
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            p: {
              xs: 1.15,
              sm: 1.25,
            },

            mb: 1.6,

            borderRadius: 3,

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 5px 16px rgba(15, 23, 42, 0.025)",
          }}
        >
          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",

                sm:
                  "repeat(2, minmax(0, 1fr))",

                lg:
                  "minmax(0, 1fr) 220px 125px 135px",
              },

              gap: 1,

              alignItems: "center",
            }}
          >
            <TextField
              fullWidth
              size="small"

              label="Recherche"

              placeholder="Produit, code, shop ou entrepôt..."

              value={
                search
              }

              onChange={(
                event
              ) =>
                setSearch(
                  event.target.value
                )
              }
            />


            <TextField
              select
              fullWidth
              size="small"

              label="Type de lieu"

              value={
                locationFilter
              }

              onChange={(
                event
              ) =>
                setLocationFilter(
                  event.target.value
                )
              }
            >
              <MenuItem value="">
                Tous
              </MenuItem>

              <MenuItem value="store">
                Shop
              </MenuItem>

              <MenuItem value="warehouse">
                Entrepôt
              </MenuItem>
            </TextField>


            <Button
              variant="contained"

              disableElevation

              onClick={
                loadStockBalances
              }

              disabled={
                loading
              }

              sx={{
                minWidth: 0,
                width: "100%",

                whiteSpace:
                  "nowrap",
              }}
            >
              {loading
                ? "Chargement..."
                : "Actualiser"}
            </Button>


            <Button
              variant="outlined"

              onClick={
                resetFilters
              }

              sx={{
                minWidth: 0,
                width: "100%",

                whiteSpace:
                  "nowrap",
              }}
            >
              Réinitialiser
            </Button>
          </Box>
        </Paper>


        {/* =================================================
            REGISTER
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            borderRadius: 3.5,

            overflow: "hidden",

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 8px 24px rgba(15, 23, 42, 0.035)",
          }}
        >
          <Box
            sx={{
              px: {
                xs: 1.4,
                sm: 1.8,
              },

              py: 1.15,

              display: "flex",

              flexDirection: {
                xs: "column",
                sm: "row",
              },

              justifyContent:
                "space-between",

              alignItems: {
                xs: "flex-start",
                sm: "center",
              },

              gap: 0.8,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",

              bgcolor: "#ffffff",
            }}
          >
            <Box>
              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                Registre du stock actuel
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Produit, emplacement, quantité réservée et
                disponible
              </Typography>
            </Box>


            <Chip
              size="small"

              variant="outlined"

              label={`${filteredStockBalances.length} ligne${
                filteredStockBalances.length >
                1
                  ? "s"
                  : ""
              }`}
            />
          </Box>


          {/* =================================================
              DESKTOP / TABLET
          ================================================= */}

          {!isMdDown ? (
            <Box
              sx={{
                width: "100%",

                minWidth: 0,

                overflowX: "auto",

                bgcolor: "#ffffff",
              }}
            >
              <Box
                sx={{
                  minWidth: 820,
                }}
              >
                <DataGrid
                  autoHeight

                  rows={
                    filteredStockBalances
                  }

                  columns={
                    columns
                  }

                  loading={
                    loading
                  }

                  disableRowSelectionOnClick

                  disableColumnSelector

                  pagination

                  paginationMode="client"

                  paginationModel={
                    paginationModel
                  }

                  onPaginationModelChange={(
                    model
                  ) =>
                    setPaginationModel({
                      page:
                        model.page,

                      pageSize:
                        ROWS_PER_PAGE,
                    })
                  }

                  pageSizeOptions={[
                    ROWS_PER_PAGE,
                  ]}

                  rowHeight={78}

                  columnHeaderHeight={48}

                  localeText={{
                    noRowsLabel:
                      "Aucune ligne de stock trouvée",

                    noResultsOverlayLabel:
                      "Aucun résultat",

                    MuiTablePagination:
                      {
                        labelRowsPerPage:
                          "",

                        labelDisplayedRows:
                          ({
                            from,
                            to,
                            count,
                          }) =>
                            `${from}–${to} sur ${count}`,
                      },
                  }}

                  sx={{
                    border: 0,

                    width: "100%",

                    color: "#334155",

                    "& .MuiDataGrid-columnHeaders":
                      {
                        bgcolor:
                          "#f8fafc",

                        borderBottom:
                          "1px solid rgba(148, 163, 184, 0.22)",
                      },

                    "& .MuiDataGrid-columnHeader":
                      {
                        bgcolor:
                          "#f8fafc",

                        px: 1,
                      },

                    "& .MuiDataGrid-columnHeaderTitle":
                      {
                        fontWeight: 900,

                        color:
                          "#334155",

                        fontSize:
                          "0.78rem",
                      },

                    "& .MuiDataGrid-cell":
                      {
                        px: 1,

                        minWidth: 0,

                        display: "flex",

                        alignItems:
                          "center",

                        overflow:
                          "hidden",

                        borderColor:
                          "rgba(148, 163, 184, 0.14)",
                      },

                    "& .MuiDataGrid-row":
                      {
                        minHeight:
                          "78px !important",

                        maxHeight:
                          "78px !important",
                      },

                    "& .MuiDataGrid-row:hover":
                      {
                        bgcolor:
                          "#f8fbff",
                      },

                    "& .MuiDataGrid-footerContainer":
                      {
                        minHeight: 50,

                        bgcolor:
                          "#ffffff",

                        borderTop:
                          "1px solid rgba(148, 163, 184, 0.20)",
                      },

                    "& .MuiTablePagination-toolbar":
                      {
                        minHeight: 50,
                      },

                    "& .MuiTablePagination-selectLabel":
                      {
                        display: "none",
                      },

                    "& .MuiTablePagination-input":
                      {
                        display: "none",
                      },

                    "& .MuiDataGrid-virtualScroller":
                      {
                        overflowX:
                          "auto",
                      },
                  }}
                />
              </Box>
            </Box>
          ) : (

            /* =================================================
               MOBILE / NARROW TABLET
            ================================================= */

            <Box
              sx={{
                p: {
                  xs: 1.1,
                  sm: 1.25,
                },

                bgcolor: "#f8fafc",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    py: 5,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Chargement du stock...
                  </Typography>
                </Box>
              ) : paginatedMobileRows.length ===
                0 ? (
                <Box
                  sx={{
                    py: 5,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Aucune ligne de stock trouvée.
                  </Typography>
                </Box>
              ) : (
                <Stack
                  spacing={1}
                >
                  {paginatedMobileRows.map(
                    (row) => (
                      <StockMobileCard
                        key={
                          row.id
                        }

                        row={
                          row
                        }

                        canManage={
                          canManageStock
                        }

                        onEdit={
                          openEditDialog
                        }

                        onDelete={
                          handleDelete
                        }
                      />
                    )
                  )}
                </Stack>
              )}


              <TablePagination
                component="div"

                count={
                  filteredStockBalances.length
                }

                page={
                  paginationModel.page
                }

                onPageChange={(
                  _event,
                  newPage
                ) =>
                  setPaginationModel(
                    (previous) => ({
                      ...previous,

                      page:
                        newPage,

                      pageSize:
                        ROWS_PER_PAGE,
                    })
                  )
                }

                rowsPerPage={
                  ROWS_PER_PAGE
                }

                rowsPerPageOptions={[
                  ROWS_PER_PAGE,
                ]}

                labelRowsPerPage=""

                labelDisplayedRows={({
                  from,
                  to,
                  count,
                }) =>
                  `${from}–${to} sur ${count}`
                }

                sx={{
                  mt: 1,

                  bgcolor:
                    "#ffffff",

                  borderRadius: 2.5,

                  "& .MuiTablePagination-toolbar":
                    {
                      minHeight: 50,
                      px: 0.8,
                    },

                  "& .MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display: "none",
                    },
                }}
              />
            </Box>
          )}
        </Paper>
      </Box>


      {/* =================================================
          STOCK DIALOG
      ================================================= */}

      <Dialog
        open={
          open
        }

        onClose={
          handleClose
        }

        fullWidth

        maxWidth="sm"

        fullScreen={
          isSmDown
        }

        PaperProps={{
          sx: {
            borderRadius: {
              xs: 0,
              sm: 4,
            },

            m: {
              xs: 0,
              sm: 2,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.3,
          }}
        >
          <Typography
            fontWeight={900}
            fontSize={20}
            color="#0f172a"
          >
            {selectedStockBalance
              ? "Modifier le stock"
              : "Ajouter une ligne de stock"}
          </Typography>


          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.3,
            }}
          >
            {selectedStockBalance
              ? "Modifiez les quantités de cette ligne de stock."
              : "Créez manuellement une ligne de stock dans un shop ou un entrepôt."}
          </Typography>
        </DialogTitle>


        <DialogContent
          dividers
        >
          <Stack
            spacing={2}

            sx={{
              pt: 0.5,
            }}
          >
            <Alert
              severity="warning"
              variant="outlined"
              sx={{
                borderRadius: 3,
              }}
            >
              Modification manuelle réservée aux rôles
              autorisés. Pour les gérants, le stock doit
              évoluer par réception, vente, transfert ou audit
              validé.
            </Alert>


            <Paper
              variant="outlined"

              sx={{
                p: 1.4,

                borderRadius: 3,

                borderColor:
                  "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  mb: 1.1,
                }}
              >
                Produit
              </Typography>


              <TextField
                select

                size="small"

                label="Produit"

                name="product_id"

                value={
                  formData.product_id
                }

                onChange={
                  handleChange
                }

                fullWidth

                required

                disabled={
                  saving
                }
              >
                {products.map(
                  (product) => (
                    <MenuItem
                      key={
                        product.id
                      }

                      value={
                        product.id
                      }
                    >
                      {product.code} -{" "}
                      {product.name}
                    </MenuItem>
                  )
                )}
              </TextField>
            </Paper>


            <Paper
              variant="outlined"

              sx={{
                p: 1.4,

                borderRadius: 3,

                bgcolor: "#f8fafc",

                borderColor:
                  "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  mb: 1.1,
                }}
              >
                Emplacement
              </Typography>


              <Stack
                spacing={1.3}
              >
                <TextField
                  select

                  size="small"

                  label="Type de lieu"

                  name="location_type"

                  value={
                    formData.location_type
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                >
                  <MenuItem value="store">
                    Shop
                  </MenuItem>

                  <MenuItem value="warehouse">
                    Entrepôt
                  </MenuItem>
                </TextField>


                {formData.location_type ===
                  "store" && (
                  <TextField
                    select

                    size="small"

                    label="Shop"

                    name="store_id"

                    value={
                      formData.store_id
                    }

                    onChange={
                      handleChange
                    }

                    fullWidth

                    required

                    disabled={
                      saving
                    }
                  >
                    {stores.map(
                      (store) => (
                        <MenuItem
                          key={
                            store.id
                          }

                          value={
                            store.id
                          }
                        >
                          {store.code} -{" "}
                          {store.name}
                        </MenuItem>
                      )
                    )}
                  </TextField>
                )}


                {formData.location_type ===
                  "warehouse" && (
                  <TextField
                    select

                    size="small"

                    label="Entrepôt"

                    name="warehouse_id"

                    value={
                      formData.warehouse_id
                    }

                    onChange={
                      handleChange
                    }

                    fullWidth

                    required

                    disabled={
                      saving
                    }
                  >
                    {warehouses.map(
                      (warehouse) => (
                        <MenuItem
                          key={
                            warehouse.id
                          }

                          value={
                            warehouse.id
                          }
                        >
                          {warehouse.code} -{" "}
                          {warehouse.name}
                        </MenuItem>
                      )
                    )}
                  </TextField>
                )}
              </Stack>
            </Paper>


            <Paper
              variant="outlined"

              sx={{
                p: 1.4,

                borderRadius: 3,

                borderColor:
                  "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  mb: 1.1,
                }}
              >
                Quantités
              </Typography>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns: {
                    xs: "1fr",

                    sm:
                      "repeat(2, minmax(0, 1fr))",
                  },

                  gap: 1.3,

                  mb: 1.3,
                }}
              >
                <TextField
                  size="small"

                  label="Quantité"

                  name="quantity"

                  type="number"

                  value={
                    formData.quantity
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  disabled={
                    saving
                  }

                  slotProps={{
                    htmlInput: {
                      min: 0,
                      step: "0.01",
                    },
                  }}
                />


                <TextField
                  size="small"

                  label="Quantité réservée"

                  name="reserved_quantity"

                  type="number"

                  value={
                    formData.reserved_quantity
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  disabled={
                    saving
                  }

                  slotProps={{
                    htmlInput: {
                      min: 0,
                      step: "0.01",
                    },
                  }}
                />
              </Box>


              <TextField
                size="small"

                label="Quantité disponible"

                value={formatQuantity(
                  Number(
                    formData.quantity ||
                      0
                  ) -
                    Number(
                      formData.reserved_quantity ||
                        0
                    )
                )}

                fullWidth

                InputProps={{
                  readOnly: true,
                }}

                sx={{
                  "& .MuiInputBase-input":
                    {
                      fontWeight: 900,

                      color:
                        Number(
                          formData.quantity ||
                            0
                        ) -
                          Number(
                            formData.reserved_quantity ||
                              0
                          ) >
                        0
                          ? "#16a34a"
                          : "#dc2626",
                    },
                }}
              />
            </Paper>
          </Stack>
        </DialogContent>


        <DialogActions
          sx={{
            px: {
              xs: 2,
              sm: 3,
            },

            py: 2,

            gap: 1,

            borderTop:
              "1px solid",

            borderColor:
              "divider",
          }}
        >
          <Button
            onClick={
              handleClose
            }

            disabled={
              saving
            }
          >
            Annuler
          </Button>


          <Button
            variant="contained"

            disableElevation

            onClick={
              handleSave
            }

            disabled={
              saving
            }
          >
            {saving
              ? "Enregistrement..."
              : selectedStockBalance
              ? "Modifier"
              : "Enregistrer"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* =================================================
          SNACKBAR
      ================================================= */}

      <Snackbar
        open={
          snackbar.open
        }

        autoHideDuration={
          3500
        }

        onClose={
          handleCloseSnackbar
        }

        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={
            handleCloseSnackbar
          }

          severity={
            snackbar.severity
          }

          variant="filled"

          sx={{
            width: "100%",

            borderRadius: 3,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}


export default StockBalancesPage
